<template>
  <router-link
    :to="{
      name: jobDetailRouteName,
      params: { role_id: props.role.id },
      query: route.query,
      replace: true
    }"
    class="group flex gap-4 rounded-xl border bg-white text-sm list-none cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cosmicPurple"
    :class="[
      isMinimized ? 'p-3' : 'px-4 py-3',
      isRoleExpanded
        ? 'border-cosmicPurple ring-1 ring-cosmicPurple bg-cosmicPurple/[0.03]'
        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/60'
    ]"
    :id="`role-${props.role.id}`"
  >
    <!-- Logo -->
    <img
      v-if="props.role?.employer_details?.logo"
      :src="props.role.employer_details.logo"
      :alt="`${props.role.employer_details.name ?? 'Employer'} logo`"
      class="h-10 w-10 shrink-0 rounded-lg bg-white object-contain ring-1 ring-black/5"
      loading="lazy"
    />
    <span
      v-else
      class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 ring-1 ring-black/5"
    >
      <Building class="h-5 w-5 text-gray-400" />
    </span>

    <article class="min-w-0 flex-1">
      <!-- Title, company, date -->
      <div class="flex items-start gap-3">
        <div class="min-w-0 flex-1">
          <h2
            class="flex items-center gap-2 text-[15px] font-semibold leading-snug"
            :class="props.role.status_open ? 'text-gray-900' : 'text-gray-500'"
          >
            <span :class="isMinimized ? 'line-clamp-2' : 'truncate'">{{ props.role.role_name }}</span>
            <ScrapedBadge v-if="props.role.is_scraped" class="shrink-0" />
            <span
              v-if="!props.role.status_open"
              class="shrink-0 rounded-full bg-gray-100 px-2 text-xs font-medium text-gray-500"
            >
              Closed
            </span>
          </h2>
          <p class="mt-0.5 truncate text-gray-500">{{ props.role?.employer_details?.name }}</p>
        </div>
        <span v-if="!isMinimized" class="shrink-0 whitespace-nowrap text-xs text-gray-400">
          <TimeAgo :date="props.role.created_at" />
        </span>
      </div>

      <!-- Metadata pills -->
      <div
        v-if="!isMinimized"
        class="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500"
      >
        <LocationDisplay :data="props.role" />
        <span class="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2">
          <ChartNoAxesColumn class="h-3 w-3" />
          <SeniorityLabel class="!text-xs" :seniority-level="props.role.seniority_level as string" />
        </span>
        <span
          v-if="hasApplied"
          class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 text-emerald-700"
        >
          <Check class="h-3 w-3" />
          In my applications
        </span>
        <!-- Applicants — hidden for limited users (Common / unsupported Fellows) -->
        <span
          v-if="!isLimitedUser && applicantNames"
          class="inline-flex min-w-0 items-center gap-1.5"
        >
          <UsersRound class="h-3 w-3 shrink-0" />
          <span class="truncate" :class="{ 'privacy-blur': isBlurred }">{{ applicantNames }}</span>
        </span>
      </div>
      <span
        v-else-if="hasApplied"
        class="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 text-xs text-emerald-700"
      >
        <Check class="h-3 w-3" />
        Added
      </span>
    </article>
  </router-link>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SeniorityLabel from '@/components/common/SeniorityLabel.vue'
import TimeAgo from '@/components/common/TimeAgo.vue'
import ScrapedBadge from '@/components/common/badges/ScrapedBadge.vue'
import LocationDisplay from '@/components/common/LocationDisplay.vue'
import { usePrivacy } from '@/composables/usePrivacy'
import { useAuth } from '@/composables/useAuth'
import { UsersRound, ChartNoAxesColumn, Building, Check } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import type { OpenRole } from '@/types/openRole'

const MAX_APPLICANT_NAMES = 3

const props = defineProps<{
  role: OpenRole
}>()

const route = useRoute()
const { isBlurred } = usePrivacy()
const { isLimitedUser } = useAuth()
const isMinimized = computed(() => !!route.params.role_id)
const isRoleExpanded = computed(
  () => route.params.role_id && route.params.role_id == props.role.id.toString()
)
const jobDetailRouteName = computed(() =>
  route.path.startsWith('/explore/') ? 'Public Job Details' : 'Job details'
)

const hasApplied = computed(() => props.role.my_application_id != null)

const applicantNames = computed(() => {
  const applications = props.role?.role_applications ?? []
  const names = applications
    .slice(0, MAX_APPLICANT_NAMES)
    .map((a) => `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim())
  const extra = applications.length - names.length
  return names.join(', ') + (extra > 0 ? ` +${extra}` : '')
})
</script>
